import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useSearchParams, useNavigate, Link } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import ProductDetailsModal from '../../components/ProductDetailsModal';
import LoginModal from '../../components/LoginModal/LoginModal';
import { useInquiry } from '../../context/InquiryContext';
import { useAuth } from '../../context/AuthContext';
import { useCurrency } from '../../context/CurrencyContext';
import { getPlanConfig, shouldShowVerifiedBadge } from '../../utils/membershipUtils';
import { LOGO_PLACEHOLDER } from '../../utils/placeholders';
import VerificationBadge from '../../components/VerificationBadge/VerificationBadge';
import { API_BASE_URL } from '../../config/api';
import { useCountries } from '../../hooks/useCountries';
import './SubcategoryPage.css';
import '../HomePage/HomePage.css';

const SubcategoryPage = () => {
  const { categorySlug, subcategorySlug } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Auth context
  const { isAuthenticated } = useAuth();
  const { formatPriceRange, selectedCurrency } = useCurrency();
  
  // Inquiry context
  const { addToInquiry, isInInquiry, removeFromInquiry } = useInquiry();
  
  const [category, setCategory] = useState(null);
  const [subcategory, setSubcategory] = useState(null);
  const [subcategories, setSubcategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showProductDetailsModal, setShowProductDetailsModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [addedProducts, setAddedProducts] = useState({});
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [countrySearchQuery, setCountrySearchQuery] = useState('');
  const { countries: dynamicCountries, loading: countriesLoading } = useCountries();
  const [filters, setFilters] = useState({
    brands: [],
    membership: [],
    verification: [],
    country: [],
    businessType: []
  });

  const [expandedFilters, setExpandedFilters] = useState({
    brand: true,
    membership: true,
    verification: true,
    country: true,
    businessType: false
  });

  const brandOptions = [
    { name: 'Lafarage', count: 12 },
    { name: 'Boral', count: 8 },
    { name: 'ACC', count: 15 },
    { name: 'Ambuja', count: 10 },
  ];

  const membershipOptions = [
    { name: 'Basic', count: 49, icon: '/uploads/builder-images/d8ee44da49d1e0c28cf5bfe995ca6aa7f62420ea_w44.png' },
    { name: 'GCC', count: 23, icon: '/uploads/builder-images/cdcdf2f44d98a4cc6cdc74b524bc4359f623ed6c_w44.png' },
    { name: 'Global', count: 15, icon: '/uploads/builder-images/050559d54da8d35ba6d11841efdb737d4c3ae660_w44.png' },
  ];

  const filteredCountries = dynamicCountries.filter(c =>
    c.name.toLowerCase().includes(countrySearchQuery.toLowerCase())
  );

  const businessTypeOptions = [
    { name: 'Retailer', count: 43 },
    { name: 'Exporter', count: 23 },
    { name: 'Manufacturer', count: 23 },
  ];

  // Fetch category and subcategory info
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch all categories
        const categoriesRes = await fetch(`${API_BASE_URL}/categories`);
        if (categoriesRes.ok) {
          const categoriesData = await categoriesRes.json();
          
          // Find the matching category
          const foundCategory = categoriesData.find(
            c => c.slug === categorySlug || c.id.toString() === categorySlug
          );
          
          if (foundCategory) {
            setCategory(foundCategory);
            setSubcategories(foundCategory.subcategories || []);
            
            // Find the matching subcategory
            if (subcategorySlug) {
              const foundSubcategory = foundCategory.subcategories?.find(
                s => s.slug === subcategorySlug || 
                   s.slug?.includes(subcategorySlug) ||
                   s.name.toLowerCase() === subcategorySlug.toLowerCase()
              );
              setSubcategory(foundSubcategory || null);
              
              if (foundSubcategory) {
                // --- Tier 1: try subcategoryId filter ---
                let productsArray = [];
                try {
                  const r1 = await fetch(`${API_BASE_URL}/products?subcategory=${foundSubcategory.id}&limit=100`);
                  if (r1.ok) {
                    const d1 = await r1.json();
                    productsArray = Array.isArray(d1) ? d1 : (d1.products || []);
                    productsArray = productsArray.filter(p => p.status === 'published');
                  }
                } catch (_) {}

                // --- Tier 2: subcategory slug filter (catches slug-based DB links) ---
                if (productsArray.length === 0 && foundSubcategory.slug) {
                  try {
                    const r2 = await fetch(`${API_BASE_URL}/products?subcategory=${encodeURIComponent(foundSubcategory.slug)}&limit=100`);
                    if (r2.ok) {
                      const d2 = await r2.json();
                      let arr = Array.isArray(d2) ? d2 : (d2.products || []);
                      productsArray = arr.filter(p => p.status === 'published');
                    }
                  } catch (_) {}
                }

                // --- Tier 3: fetch all category products + client-side subcategory filter ---
                if (productsArray.length === 0) {
                  try {
                    const r3 = await fetch(`${API_BASE_URL}/products?category=${foundCategory.id}&limit=200`);
                    if (r3.ok) {
                      const d3 = await r3.json();
                      const catProds = (Array.isArray(d3) ? d3 : (d3.products || [])).filter(p => p.status === 'published');
                      // Client-side filter: match subcategory by id, slug, or name
                      const subFiltered = catProds.filter(p => {
                        const pSubId = p.subcategoryId || p.subcategory?.id;
                        const pSubSlug = p.subcategorySlug || p.subcategory?.slug || '';
                        const pSubName = p.subcategoryName || p.subcategory?.name || '';
                        return (
                          pSubId === foundSubcategory.id ||
                          pSubSlug.toLowerCase() === (foundSubcategory.slug || '').toLowerCase() ||
                          pSubName.toLowerCase() === (foundSubcategory.name || '').toLowerCase()
                        );
                      });
                      // If subcategory client-filter found results use them, otherwise fall back to all category products
                      productsArray = subFiltered.length > 0 ? subFiltered : catProds;
                    }
                  } catch (_) {}
                }

                setProducts(productsArray);
              } else {
                // Subcategory not found in categories list — fall back to category products
                const productsRes = await fetch(`${API_BASE_URL}/products?category=${foundCategory.id}&limit=100`);
                if (productsRes.ok) {
                  const productsData = await productsRes.json();
                  const productsArray = Array.isArray(productsData) ? productsData : (productsData.products || []);
                  setProducts(productsArray.filter(p => p.status === 'published'));
                }
              }
            } else {
              // No subcategory — fetch all products for category
              const productsRes = await fetch(`${API_BASE_URL}/products?category=${foundCategory.id}&limit=100`);
              if (productsRes.ok) {
                const productsData = await productsRes.json();
                const productsArray = Array.isArray(productsData) ? productsData : (productsData.products || []);
                setProducts(productsArray.filter(p => p.status === 'published'));
              }
            }
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [categorySlug, subcategorySlug]);

  const toggleFilter = (filterName) => {
    setExpandedFilters(prev => ({
      ...prev,
      [filterName]: !prev[filterName]
    }));
  };
  // Handle inquire button click
  const handleInquireClick = (product, e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Check if user is logged in first
    if (!isAuthenticated) {
      setShowLoginModal(true);
      return;
    }
    
    const seller = product.seller || {};
    const productData = {
      id: product.id,
      image: product.image || product.images?.[0] || "https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=500",
      description: product.description || product.name,
      priceMin: product.priceMin || product.price || 0,
      priceMax: product.priceMax || (product.price ? product.price * 1.2 : 0),
      priceUnit: selectedCurrency.code,
      pricePer: product.pricePer || product.unit || 'Unit',
      moq: product.moq || product.minOrder || '1 Unit',
      companyName: seller.name || product.companyName || 'Unknown Seller',
      sellerId: seller.userId || product.userId,
      sellerName: seller.name || product.companyName || 'Unknown Seller',
      sellerAvatar: seller.logo || LOGO_PLACEHOLDER,
      country: seller.country || product.country || 'UAE',
      countryFlag: seller.countryFlag || 'https://flagcdn.com/w80/ae.png'
    };
    
    if (isInInquiry(product.id)) {
      removeFromInquiry(product.id);
      setAddedProducts(prev => ({ ...prev, [product.id]: false }));
    } else {
      addToInquiry(productData);
      setAddedProducts(prev => ({ ...prev, [product.id]: true }));
      setTimeout(() => {
        setAddedProducts(prev => ({ ...prev, [product.id]: 'done' }));
      }, 2000);
    }
  };

  // Handle view mobile button click - navigate to product details page
  const handleViewMobileClick = (product, e) => {
    e.preventDefault();
    e.stopPropagation();
    navigate(`/product/${product.id}`);
  };
  const handleSubcategoryClick = (sub) => {
    navigate(`/category/${categorySlug}/${sub.slug}`);
  };

  const Checkbox = ({ checked = false }) => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      {checked ? (
        <>
          <rect x="3" y="3" width="18" height="18" rx="3" fill="#E8792B"/>
          <path d="M9 12L11 14L15 10" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </>
      ) : (
        <path fillRule="evenodd" clipRule="evenodd" d="M18 4.5H6C5.17157 4.5 4.5 5.17157 4.5 6V18C4.5 18.8284 5.17157 19.5 6 19.5H18C18.8284 19.5 19.5 18.8284 19.5 18V6C19.5 5.17157 18.8284 4.5 18 4.5ZM6 3C4.34315 3 3 4.34315 3 6V18C3 19.6569 4.34315 21 6 21H18C19.6569 21 21 19.6569 21 18V6C21 4.34315 19.6569 3 18 3H6Z" fill="#6D6D6D"/>
      )}
    </svg>
  );

  const ChevronUp = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M7 13.5L12 8.5L17 13.5" stroke="#505050" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  const ChevronDown = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M17 10.5L12 15.5L7 10.5" stroke="#505050" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  const ChevronRight = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M6 13L10 9L6 5" stroke="#505050" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  const VerifiedBadge = () => (
    <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M22 11C22 11.9323 20.7329 12.6871 20.5034 13.5472C20.2653 14.4377 20.9782 15.7221 20.528 16.5015C20.0705 17.2923 18.597 17.314 17.9562 17.9562C17.314 18.5984 17.2923 20.0705 16.5014 20.528C15.7221 20.9782 14.4377 20.2653 13.5472 20.5034C12.6871 20.7329 11.9337 22 11 22C10.0663 22 9.31291 20.7329 8.45276 20.5034C7.56231 20.2653 6.27788 20.9782 5.49856 20.528C4.70769 20.0705 4.68605 18.597 4.04382 17.9562C3.4016 17.3154 1.92954 17.2923 1.47205 16.5015C1.02177 15.7221 1.73472 14.4377 1.49659 13.5472C1.26712 12.6871 0 11.9338 0 11C0 10.0663 1.26712 9.3129 1.49659 8.4528C1.73472 7.5623 1.02177 6.2779 1.47205 5.49857C1.92954 4.7077 3.40305 4.68605 4.04382 4.04383C4.68605 3.40161 4.70769 1.92955 5.49856 1.47206C6.27788 1.02178 7.56231 1.73472 8.45276 1.49659C9.31291 1.26712 10.0663 0 11 0C11.9337 0 12.6871 1.26712 13.5472 1.49659C14.4377 1.73472 15.7221 1.02178 16.5014 1.47206C17.2923 1.92955 17.314 3.40305 17.9562 4.04383C18.5984 4.68461 20.0705 4.7077 20.528 5.49857C20.9782 6.2779 20.2653 7.5623 20.5034 8.4528C20.7329 9.3129 22 10.0663 22 11Z" fill="#29ABE2"/>
      <path d="M16.5881 7.6015C16.1234 7.1368 15.3701 7.1368 14.9054 7.6015L10.0317 12.4751L8.00835 10.4518C7.54364 9.9871 6.79029 9.9871 6.32558 10.4518C5.86087 10.9165 5.86087 11.6698 6.32558 12.1345L9.14847 14.9574C9.39237 15.2013 9.7142 15.3168 10.0331 15.3052C10.3521 15.3168 10.6739 15.2013 10.9178 14.9574L16.5896 9.2857C17.0543 8.821 17.0543 8.0676 16.5896 7.6029L16.5881 7.6015Z" fill="white"/>
    </svg>
  );

  const InspectedBadge = () => (
    <svg width="20" height="22" viewBox="0 0 20 22" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M18.4037 10.4509C18.4037 5.66016 14.5058 1.76367 9.71649 1.76367C4.92718 1.76367 1.0293 5.66156 1.0293 10.4509C1.0293 15.2402 4.92718 19.1381 9.71649 19.1381C14.5058 19.1381 18.4037 15.2416 18.4037 10.4509ZM9.71649 17.8752C5.62238 17.8752 2.29075 14.5436 2.29075 10.4495C2.29075 6.35536 5.62238 3.02373 9.71649 3.02373C13.8106 3.02373 17.1422 6.35536 17.1422 10.4495C17.1422 14.5436 13.8106 17.8752 9.71649 17.8752Z" fill="#760BA8"/>
      <path d="M9.72019 16.2156C12.8289 16.2156 15.3491 13.6415 15.3491 10.4662C15.3491 7.2909 12.8289 4.7168 9.72019 4.7168C6.61145 4.7168 4.09131 7.2909 4.09131 10.4662C4.09131 13.6415 6.61145 16.2156 9.72019 16.2156Z" fill="#0EC936"/>
      <path d="M20.9429 20.2726C20.8308 19.7414 20.5561 19.2494 20.1468 18.8527L17.4501 16.3326C18.6947 14.6997 19.4348 12.6618 19.4348 10.4557C19.4348 5.0973 15.0757 0.73828 9.71738 0.73828C4.35901 0.73828 0 5.0973 0 10.4557C0 15.814 4.35901 20.1731 9.71738 20.1731C11.3811 20.1731 12.9481 19.7526 14.3189 19.0125L16.4465 22.2208C16.818 22.696 17.3281 23.0365 17.886 23.1809C18.0654 23.2272 18.2728 23.2608 18.4999 23.2608C18.9919 23.2608 19.5749 23.1038 20.1538 22.5838C21.041 21.7863 21.0732 20.8879 20.9443 20.274L20.9429 20.2726ZM9.71738 19.1429C4.92666 19.1429 1.03018 15.2464 1.03018 10.4557C1.03018 5.66495 4.92807 1.76846 9.71738 1.76846C14.5067 1.76846 18.4046 5.66636 18.4046 10.4557C18.4046 15.245 14.5067 19.1429 9.71738 19.1429Z" fill="#760BA8"/>
    </svg>
  );

  const CertifiedBadge = () => (
    <svg width="18" height="24" viewBox="0 0 18 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M15.6913 11.6414V11.6693C15.7603 13.841 15.8201 15.7152 14.5025 17.4459C13.0681 19.3321 11.0824 20.8383 8.9029 21.6977C7.2772 21.0575 5.75642 20.0626 4.50129 18.8127C3.53304 17.8484 2.50636 16.6888 2.23541 15.3898C2.06009 14.5437 2.08665 13.5621 2.11188 12.6137C2.11985 12.3334 2.12782 12.0146 2.13048 11.7118C2.13048 11.7078 2.13048 11.7038 2.13048 11.6999V11.6932C2.13314 11.2748 2.13845 10.8591 2.14509 10.4566C2.16501 9.25451 2.20087 8.00992 2.25532 6.75338L8.90423 4.3014L12.9326 5.78773L14.383 4.20974L8.90423 2.18945L0.223209 5.39191L0.192663 6.03081C0.101018 7.97274 0.0492132 9.88146 0.0372595 11.7025C0.0359314 11.9881 0.0279616 12.2816 0.0199925 12.5646C-0.0092276 13.6192 -0.0384516 14.711 0.182027 15.7683C0.555247 17.5602 1.80508 18.996 2.98052 20.1649C4.55177 21.7283 6.47098 22.9423 8.53233 23.6769L8.90423 23.8097L9.27611 23.6769C10.6587 23.1841 11.9657 22.4841 13.1611 21.5981C14.3166 20.7414 15.3406 19.7292 16.2053 18.5922C16.9889 17.5615 17.471 16.4099 17.6795 15.0737C17.8575 13.9274 17.8217 12.8036 17.7845 11.6122V11.6069C17.7738 11.2775 17.7632 10.9361 17.7579 10.5987C17.7513 10.2149 17.7446 9.82833 17.7353 9.4511" fill="#8BC105"/>
      <path d="M17.8111 6.51978L16.2359 5.04806C15.9968 4.82492 15.6262 4.84086 15.4071 5.08393L15.1507 5.36685L14.1918 6.42814L9.22968 11.9165C9.00654 12.1636 8.62536 12.1742 8.38894 11.9405L5.70601 9.28392C5.47358 9.05412 5.103 9.05943 4.87721 9.29454L3.38433 10.8526C3.15854 11.089 3.16254 11.4663 3.39497 11.6961L8.52043 16.7727C8.75685 17.0078 9.13803 16.9958 9.36117 16.7488L15.5837 9.8657L16.9584 8.34484L17.8456 7.36324C18.0648 7.1215 18.0488 6.74426 17.8111 6.52111V6.51978Z" fill="#8BC105"/>
    </svg>
  );

  // Mobile 3-tab drill-down state
  const [activeMobileTab, setActiveMobileTab] = useState('Products');

  // Group products by seller for the Suppliers tab
  const groupedBySeller = useMemo(() => {
    const map = {};
    products.forEach(p => {
      const sid = p.seller?.id;
      if (!sid) return;
      if (!map[sid]) map[sid] = { seller: p.seller, products: [] };
      map[sid].products.push(p);
    });
    return Object.values(map);
  }, [products]);

  // Use fetched products directly - no more mock data
  const displayProducts = products;

  // Render product card - same design as HomePage
  const renderProductCard = (product) => {
    const seller = product.seller || {};
    const displayCompanyName = seller.name || product.companyName || 'Unknown Seller';
    const displayLogo = seller.logo || LOGO_PLACEHOLDER;
    
    const handleCardClick = (e) => {
      if (e.target.closest('button')) return;
      navigate(`/product/${product.id}`);
    };
    
    return (
      <div key={product.id} className="featured-product-card" onClick={handleCardClick} style={{ cursor: 'pointer' }}>
        {/* Product Image Section */}
        <div className="featured-product-image-section">
          <img 
            src={product.image || product.images?.[0] || "https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=500"} 
            alt={product.name} 
            className="featured-product-img"
          />
        </div>
        
        {/* Product Details Section */}
        <div className="featured-product-details-section">
          <div className="featured-info-block">
            <div className="featured-desc-block">
              <p className="featured-product-name">
                {product.name || 'Unknown Product'}
              </p>
              
              <div className="featured-price-block">
                <div className="featured-price-row">
                  <span className="featured-price-amount">{formatPriceRange(product.priceMin || product.price || 0, product.priceMax || (product.price ? Math.round(product.price * 1.2) : 0))}</span>
                  <span className="featured-price-unit"> / {product.unit || 'Unit'}</span>
                </div>
                <p className="featured-moq">
                  <span className="featured-moq-label">(MOQ) </span>
                  <span className="featured-moq-value">{product.moq || product.minOrder || '1 Unit'}</span>
                </p>
              </div>
            </div>
            
            <div className="featured-badges-block">
              <div className="featured-badges-row">
                {(() => {
                  const planConfig = getPlanConfig(seller.membershipPlan);
                  return (
                    <div className="featured-global-badge" style={{ borderColor: planConfig.color }}>
                      {planConfig.icon && (
                        <img src={planConfig.icon} alt={planConfig.label} className="featured-global-icon" />
                      )}
                      <span className="featured-global-text" style={{ color: planConfig.color }}>{planConfig.label}</span>
                    </div>
                  );
                })()}
                
                <VerificationBadge level={seller.verificationLevel || (seller.isVerified ? 'verified' : null)} size="sm" />
              </div>
              
              <div 
                className="featured-seller-company"
                onClick={(e) => {
                  e.stopPropagation();
                  if (seller.id) navigate(`/seller/${seller.id}`);
                }}
                style={{ cursor: seller.id ? 'pointer' : 'default' }}
              >
                <img src={displayLogo} alt={displayCompanyName} className="featured-company-logo" />
                <span className="featured-company-name">
                  <span className="desktop-text">{displayCompanyName}</span>
                  <span className="mobile-text">{displayCompanyName?.split(' ').length > 2 ? displayCompanyName.split(' ').slice(0, 2).join(' ') + '...' : displayCompanyName}</span>
                </span>
              </div>
              
              <div className="featured-location-block">
                <div className="featured-location-row">
                  <div className="featured-flag-wrapper">
                    <img 
                      src={seller.countryFlag || "https://flagcdn.com/w80/ae.png"} 
                      alt="Flag" 
                      className="featured-flag-img"
                    />
                  </div>
                  <span className="featured-location-text">
                    <span className="featured-country">{seller.country || 'UAE'} / </span>
                    <span className="featured-years">{seller.memberYears || 1} Year{(seller.memberYears || 1) > 1 ? 's' : ''}</span>
                  </span>
                </div>
              </div>
              
              <div className="featured-business-block">
                <div className="featured-business-row">
                  <span className="featured-business-text">{seller.businessType || 'Manufacturer'}</span>
                </div>
              </div>
            </div>
          </div>
          
          <div className="featured-actions-row">
            <button 
              className={`featured-btn-inquire ${isInInquiry(product.id) ? 'added' : ''}`}
              onClick={(e) => handleInquireClick(product, e)}
            >
              {addedProducts[product.id] === true ? '✓ Added!' : isInInquiry(product.id) ? 'Remove' : <><span className="desktop-text">Add to Quotation</span><span className="mobile-text">+ to quote</span></>}
            </button>
            <button 
              className="featured-btn-call"
              onClick={(e) => {
                e.stopPropagation();
                const phone = seller.phone || seller.phoneNumber || product.phone;
                if (phone) {
                  window.location.href = `tel:${phone}`;
                } else {
                  alert('Phone number not available for this seller');
                }
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M22 16.92V19.92C22 20.4704 21.7893 20.9981 21.4142 21.3981C21.0391 21.7982 20.5304 22.0362 19.98 22.0699C19.643 22.0899 19.2989 22.0999 18.95 22.0999C10.21 22.0999 3.10001 14.9899 3.10001 6.24994C3.10001 5.90094 3.11001 5.55694 3.13001 5.21994C3.16376 4.66947 3.40177 4.16077 3.80183 3.78568C4.20189 3.41059 4.72954 3.19994 5.28001 3.19994H8.28001C8.72424 3.19557 9.15598 3.35553 9.49332 3.64969C9.83067 3.94386 10.0507 4.35171 10.11 4.79194C10.2199 5.66094 10.4293 6.51394 10.73 7.32994C10.8567 7.66661 10.8837 8.03161 10.808 8.38227C10.7323 8.73294 10.5571 9.05427 10.3 9.30994L9.00001 10.6099C10.3612 13.1755 12.4245 15.2387 14.99 16.5999L16.29 15.2999C16.5457 15.0429 16.8671 14.8677 17.2177 14.792C17.5684 14.7163 17.9334 14.7433 18.27 14.8699C19.086 15.1707 19.9391 15.3801 20.808 15.4899C21.2532 15.5498 21.6651 15.7746 21.9605 16.1183C22.256 16.462 22.4131 16.9009 22.4 17.3499L22 16.92Z" stroke="#6D6D6D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span>Call</span>
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="subcategory-page">
      {/* ===== DESKTOP LAYOUT (hidden on mobile via CSS) ===== */}
      <div className="sp-desktop-only">
      <Header />
      
      <main className="sp-main-content">
        {/* Breadcrumb */}
        <div className="sp-breadcrumb">
          <Link to="/" className="breadcrumb-item">Home</Link>
          <ChevronRight />
          {category && (
            <>
              <Link to={`/category/${category.slug || category.id}`} className="breadcrumb-item">
                {category.name}
              </Link>
              <ChevronRight />
            </>
          )}
          <span className="breadcrumb-item active">
            {subcategory?.name || subcategorySlug || 'Products'}
          </span>
        </div>

        {/* Mobile Category Title with Filter Icon */}
        <div className="sp-mobile-title-row">
          <span className="sp-mobile-title">
            {subcategory?.name || category?.name || 'Products'}
          </span>
          <button className="sp-mobile-filter-icon" onClick={() => setIsMobileSidebarOpen(true)}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
            </svg>
          </button>
        </div>

        <div className="sp-container">
          {/* Mobile Sidebar Overlay */}
          {isMobileSidebarOpen && (
            <div 
              className="sp-mobile-sidebar-overlay active" 
              onClick={() => setIsMobileSidebarOpen(false)}
            />
          )}

          {/* Left Sidebar */}
          <aside className={`sp-sidebar ${isMobileSidebarOpen ? 'mobile-open' : ''}`}>
            {/* Mobile Close Button */}
            {isMobileSidebarOpen && (
              <button 
                onClick={() => setIsMobileSidebarOpen(false)}
                style={{
                  position: 'absolute',
                  top: '10px',
                  right: '10px',
                  background: '#fff',
                  border: '1px solid #ddd',
                  borderRadius: '50%',
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  zIndex: 1001,
                  boxShadow: '0 2px 6px rgba(0,0,0,0.12)'
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 6L6 18M6 6L18 18" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            )}
            {/* Category Section */}
            <div className="sidebar-section category-section">
              <div className="category-header">
                <span className="category-title">{category?.name || 'Categories'}</span>
              </div>
              <div className="category-list">
                {subcategories.map((sub, index) => (
                  <div 
                    key={sub.id || index} 
                    className={`category-item ${(subcategory?.slug === sub.slug || subcategorySlug === sub.slug) ? 'active' : ''}`}
                    onClick={() => handleSubcategoryClick(sub)}
                  >
                    <span>{sub.name}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Filter By Header */}
            <div className="filter-header">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M21.6197 4.64346C21.5043 4.37675 21.313 4.14986 21.0696 3.99101C20.8263 3.83216 20.5416 3.75 20.251 3.75H3.75103C3.46053 3.75 3.17591 3.83213 2.9326 3.99093C2.68929 4.14974 2.49804 4.37656 2.38266 4.64319C2.26728 4.90982 2.23277 5.20434 2.28316 5.49084C2.33354 5.77735 2.46653 6.04338 2.66603 6.2572L2.66759 6.2588L9.00103 13.0472V19.4993C9.00083 19.7522 9.06175 20.0015 9.17862 20.2257C9.29549 20.4499 9.46477 20.6422 9.67228 20.7863C9.87979 20.9304 10.1194 21.0221 10.3712 21.0537C10.6229 21.0852 10.8787 21.0558 11.1134 20.968L11.1159 20.9671L14.1159 19.9021C14.4014 19.7942 14.6488 19.6047 14.8277 19.357C15.0066 19.1094 15.1093 18.8144 15.1243 18.5088L15.126 18.4722V13.0472L21.336 6.26721L21.3435 6.25877C21.5408 6.04489 21.6707 5.77764 21.7171 5.49037C21.7634 5.2031 21.7241 4.90854 21.6041 4.64346H21.6197ZM13.7053 12.2419C13.5756 12.3795 13.5026 12.5609 13.501 12.75V18.2494L10.501 19.4143V12.75C10.501 12.5596 10.4286 12.3762 10.2985 12.2372L3.75095 5.25002H20.251L13.7053 12.2419Z" fill="#212121"/>
              </svg>
              <span className="filter-title">Filter By</span>
            </div>

            {/* Brand Filter */}
            <div className="filter-section">
              <div className="filter-section-header" onClick={() => toggleFilter('brand')}>
                <span className="filter-section-title">Brand</span>
                {expandedFilters.brand ? <ChevronUp /> : <ChevronDown />}
              </div>
              {expandedFilters.brand && (
                <>
                  <div className="filter-search">
                    <input type="text" placeholder="Search brand" className="filter-search-input" />
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M14 14L10 10M2 6.66667C2 7.2795 2.12071 7.88634 2.35523 8.45252C2.58975 9.01871 2.93349 9.53316 3.36683 9.9665C3.80018 10.3998 4.31462 10.7436 4.88081 10.9781C5.447 11.2126 6.05383 11.3333 6.66667 11.3333C7.2795 11.3333 7.88634 11.2126 8.45252 10.9781C9.01871 10.7436 9.53316 10.3998 9.9665 9.9665C10.3998 9.53316 10.7436 9.01871 10.9781 8.45252C11.2126 7.88634 11.3333 7.2795 11.3333 6.66667C11.3333 6.05383 11.2126 5.447 10.9781 4.88081C10.7436 4.31462 10.3998 3.80018 9.9665 3.36683C9.53316 2.93349 9.01871 2.58975 8.45252 2.35523C7.88634 2.12071 7.2795 2 6.66667 2C6.05383 2 5.447 2.12071 4.88081 2.35523C4.31462 2.58975 3.80018 2.93349 3.36683 3.36683C2.93349 3.80018 2.58975 4.31462 2.35523 4.88081C2.12071 5.447 2 6.05383 2 6.66667Z" stroke="#6D6D6D" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <div className="filter-options">
                    {brandOptions.map((brand, index) => (
                      <label key={index} className="filter-option">
                        <Checkbox />
                        <span className="option-name">{brand.name}</span>
                      </label>
                    ))}
                  </div>
                </>
              )}
            </div>

            <div className="filter-divider"></div>

            {/* Membership Filter */}
            <div className="filter-section">
              <div className="filter-section-header" onClick={() => toggleFilter('membership')}>
                <span className="filter-section-title">Membership plan</span>
                {expandedFilters.membership ? <ChevronUp /> : <ChevronDown />}
              </div>
              {expandedFilters.membership && (
                <div className="filter-options">
                  {membershipOptions.map((option, index) => (
                    <label key={index} className="filter-option">
                      <Checkbox />
                      <div className="membership-option">
                        <img src={option.icon} alt={option.name} className="membership-icon" />
                        <span className="option-name">{option.name} ({option.count})</span>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>

            <div className="filter-divider"></div>

            {/* Verification Filter */}
            <div className="filter-section">
              <div className="filter-section-header" onClick={() => toggleFilter('verification')}>
                <span className="filter-section-title">Verification Levels</span>
                {expandedFilters.verification ? <ChevronUp /> : <ChevronDown />}
              </div>
              {expandedFilters.verification && (
                <div className="filter-options">
                  <label className="filter-option">
                    <Checkbox />
                    <div className="verification-option">
                      <VerifiedBadge />
                      <span className="verification-text verified">Verified</span>
                      <span className="verification-count">(49)</span>
                    </div>
                  </label>
                  <label className="filter-option">
                    <Checkbox />
                    <div className="verification-option">
                      <InspectedBadge />
                      <span className="verification-text inspected">Inspected</span>
                      <span className="verification-count">(23)</span>
                    </div>
                  </label>
                  <label className="filter-option">
                    <Checkbox />
                    <div className="verification-option">
                      <CertifiedBadge />
                      <span className="verification-text certified">Certified</span>
                      <span className="verification-count">(15)</span>
                    </div>
                  </label>
                </div>
              )}
            </div>

            <div className="filter-divider"></div>

            {/* Country Filter */}
            <div className="filter-section">
              <div className="filter-section-header" onClick={() => toggleFilter('country')}>
                <span className="filter-section-title">Country</span>
                {expandedFilters.country ? <ChevronUp /> : <ChevronDown />}
              </div>
              {expandedFilters.country && (
                <>
                  <div className="filter-search-box">
                    <input
                      type="text"
                      placeholder="Search countries..."
                      value={countrySearchQuery}
                      onChange={(e) => setCountrySearchQuery(e.target.value)}
                      className="filter-search-input"
                    />
                  </div>
                  <div className="filter-options filter-options-scroll">
                    {countriesLoading ? (
                      <span className="filter-loading">Loading countries…</span>
                    ) : filteredCountries.length === 0 ? (
                      <span className="filter-empty">No countries found</span>
                    ) : (
                      filteredCountries.map((country) => (
                        <label
                          key={country.id}
                          className="filter-option"
                          onClick={(e) => {
                            e.preventDefault();
                            setFilters(prev => ({
                              ...prev,
                              country: prev.country.includes(country.name)
                                ? prev.country.filter(c => c !== country.name)
                                : [...prev.country, country.name]
                            }));
                          }}
                        >
                          <Checkbox checked={filters.country.includes(country.name)} />
                          <div className="country-option">
                            <div className="country-flag-container">
                              <img src={country.flag} alt={country.name} className="country-flag" />
                            </div>
                            <span className="option-name">{country.name}</span>
                          </div>
                        </label>
                      ))
                    )}
                  </div>
                </>
              )}
            </div>

            <div className="filter-divider"></div>

            {/* Business Type Filter */}
            <div className="filter-section">
              <div className="filter-section-header" onClick={() => toggleFilter('businessType')}>
                <span className="filter-section-title">Business Type</span>
                {expandedFilters.businessType ? <ChevronUp /> : <ChevronDown />}
              </div>
              {expandedFilters.businessType && (
                <div className="filter-options">
                  {businessTypeOptions.map((type, index) => (
                    <label key={index} className="filter-option">
                      <Checkbox />
                      <span className="option-name">{type.name} ({type.count})</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </aside>

          {/* Main Content */}
          <div className="sp-content">
            {/* Page Title */}
            <div className="sp-page-header">
              <h1 className="sp-title">{subcategory?.name || subcategorySlug || category?.name || 'Products'}</h1>
              <p className="sp-subtitle">
                {displayProducts.length} products found in {subcategory?.name || subcategorySlug || category?.name || 'this category'}
              </p>
            </div>

            {/* Banner */}
            <div className="sp-banner">
              <img 
                src="/uploads/builder-images/659dc936c0063da65c0d0cd4f76e1a113d742af6_w2144.png" 
                alt={`${subcategory?.name || 'Products'} Banner`} 
                className="sp-banner-image"
              />
            </div>

            {loading ? (
              <div className="sp-loading">
                <p>Loading products...</p>
              </div>
            ) : (
              <>
                {/* Products Grid - Using same card design as HomePage */}
                <div className="sp-products-grid-home">
                  {displayProducts.slice(0, 4).map((product) => renderProductCard(product))}
                </div>

                {/* Mid Banner */}
                <div className="sp-mid-banner">
                  <img 
                    src="/uploads/builder-images/c70e129afc69d7dc9464719e19866004ede27393_w2144.png" 
                    alt="Promotional Banner" 
                    className="sp-mid-banner-image"
                  />
                </div>

                {/* More Products */}
                <div className="sp-products-grid-home">
                  {displayProducts.slice(4).map((product) => renderProductCard(product))}
                </div>

                {displayProducts.length === 0 && (
                  <div className="sp-no-products">
                    <p>No products found in this category.</p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </main>

      <Footer />
      </div>{/* /sp-desktop-only */}

      {/* ===== MOBILE LAYOUT - Alibaba style 3-tab drill-down ===== */}
      <div className="sp-mobile-catpage">

        {/* Mobile Header */}
        <div className="sp-mob-header">
          <button className="sp-mob-back" onClick={() => navigate(-1)}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#333" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5M12 5l-7 7 7 7"/>
            </svg>
          </button>
          <div className="sp-mob-searchbar">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2">
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <span className="sp-mob-search-keyword">
              {subcategory?.name || category?.name || 'Products'}
            </span>
          </div>
          <div className="sp-mob-header-actions">
            <button className="sp-mob-icon-btn">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#333" strokeWidth="1.8">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                <circle cx="12" cy="10" r="3"/>
              </svg>
            </button>
          </div>
        </div>

        {/* Sub-info bar */}
        <div className="sp-mob-subinfo">
          <span className="sp-mob-subinfo-count">{displayProducts.length} {displayProducts.length === 1 ? 'product' : 'products'}</span>
          {(subcategory?.name || category?.name) && <span className="sp-mob-subinfo-sep"> · </span>}
          <span className="sp-mob-subinfo-name">{subcategory?.name || category?.name || ''}</span>
        </div>

        {/* Tab Bar */}
        <div className="sp-mob-tabs">
          {['Products', 'Suppliers', 'Worldwide'].map(tab => (
            <button
              key={tab}
              className={`sp-mob-tab${activeMobileTab === tab ? ' active' : ''}`}
              onClick={() => setActiveMobileTab(tab)}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="sp-mob-content">

          {/* ---- PRODUCTS TAB ---- */}
          {activeMobileTab === 'Products' && (
            <div className="sp-mob-list">
              {loading ? (
                <div className="sp-mob-state-msg">Loading products...</div>
              ) : displayProducts.length === 0 ? (
                <div className="sp-mob-state-msg">No products found</div>
              ) : displayProducts.map(product => {
                const seller = product.seller || {};
                return (
                  <div
                    key={product.id}
                    className="sp-mob-prod-row"
                    onClick={() => navigate(`/product/${product.id}`)}
                  >
                    <div className="sp-mob-prod-row-img-wrap">
                      <img
                        src={product.image || 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=300'}
                        alt={product.name}
                        className="sp-mob-prod-row-img"
                        onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=300'; }}
                      />
                    </div>
                    <div className="sp-mob-prod-row-info">
                      <p className="sp-mob-prod-row-name">{product.name}</p>
                      <p className="sp-mob-prod-row-price">{formatPriceRange(product.priceMin, product.priceMax)}</p>
                      <p className="sp-mob-prod-row-moq">Min. order: {product.moq || '1 piece'}</p>
                      <div className="sp-mob-prod-row-origin">
                        {seller.countryFlag && (
                          <img src={seller.countryFlag} alt={seller.country} className="sp-mob-prod-row-flag" />
                        )}
                        <span>{seller.country || 'UAE'}</span>
                      </div>
                      <button
                        className="sp-mob-quote-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleInquireClick(product, e);
                        }}
                      >
                        {isInInquiry(product.id) ? 'Remove' : '+ Quote'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* ---- SUPPLIERS TAB ---- */}
          {activeMobileTab === 'Suppliers' && (
            <div className="sp-mob-suppliers-list">
              {loading ? (
                <div className="sp-mob-state-msg">Loading suppliers...</div>
              ) : groupedBySeller.length === 0 ? (
                <div className="sp-mob-state-msg">No suppliers found</div>
              ) : groupedBySeller.map(({ seller, products: sellerProducts }) => (
                <div
                  key={seller.id}
                  className="sp-mob-supplier-block"
                  onClick={() => navigate(`/seller/${seller.id}`)}
                >
                  <div className="sp-mob-sup-header">
                    <img
                      src={seller.logo || LOGO_PLACEHOLDER}
                      alt={seller.name}
                      className="sp-mob-sup-logo"
                      onError={(e) => { e.target.src = LOGO_PLACEHOLDER; }}
                    />
                    <div className="sp-mob-sup-meta">
                      <span className="sp-mob-sup-name">{seller.name}</span>
                      {seller.isVerified && (
                        <span className="sp-mob-sup-verified">
                          <svg width="13" height="13" viewBox="0 0 22 22" fill="none">
                            <ellipse cx="11" cy="11" rx="11" ry="11" fill="#29ABE2"/>
                            <path d="M16.5 7.6a1.5 1.5 0 0 0-2.1 0L10 12l-2-2a1.5 1.5 0 0 0-2.1 2.1l3 3a1.5 1.5 0 0 0 2.1 0l5.5-5.5a1.5 1.5 0 0 0 0-2z" fill="#fff"/>
                          </svg>
                          Verified
                        </span>
                      )}
                      <div className="sp-mob-sup-tags">
                        {seller.businessType && <span className="sp-mob-sup-tag">{seller.businessType}</span>}
                        {seller.membershipPlan && <span className="sp-mob-sup-tag">{seller.membershipPlan}</span>}
                        {seller.memberYears > 0 && <span className="sp-mob-sup-tag">{seller.memberYears}YR</span>}
                      </div>
                    </div>
                  </div>
                  <div className="sp-mob-sup-products">
                    {sellerProducts.slice(0, 3).map(p => (
                      <img
                        key={p.id}
                        src={p.image || 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=200'}
                        alt={p.name}
                        className="sp-mob-sup-prod-thumb"
                        onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=200'; }}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ---- WORLDWIDE TAB ---- */}
          {activeMobileTab === 'Worldwide' && (
            <div className="sp-mob-list">
              {loading ? (
                <div className="sp-mob-state-msg">Loading...</div>
              ) : displayProducts.length === 0 ? (
                <div className="sp-mob-state-msg">No products available worldwide</div>
              ) : displayProducts.map(product => {
                const seller = product.seller || {};
                return (
                  <div
                    key={product.id}
                    className="sp-mob-prod-row"
                    onClick={() => navigate(`/product/${product.id}`)}
                  >
                    <div className="sp-mob-prod-row-img-wrap">
                      <img
                        src={product.image || 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=300'}
                        alt={product.name}
                        className="sp-mob-prod-row-img"
                        onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=300'; }}
                      />
                      {seller.countryFlag && (
                        <img src={seller.countryFlag} alt="" className="sp-mob-prod-row-flag-overlay" />
                      )}
                    </div>
                    <div className="sp-mob-prod-row-info">
                      <div className="sp-mob-ww-country">
                        {seller.countryFlag && (
                          <img src={seller.countryFlag} alt={seller.country} className="sp-mob-ww-flag" />
                        )}
                        <span className="sp-mob-ww-country-name">{seller.country || 'UAE'}</span>
                      </div>
                      <p className="sp-mob-prod-row-name">{product.name}</p>
                      <p className="sp-mob-prod-row-price">{formatPriceRange(product.priceMin, product.priceMax)}</p>
                      <p className="sp-mob-prod-row-moq">Min. order: {product.moq || '1 piece'}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

        </div>
      </div>

      {/* Product Details Modal */}
      <ProductDetailsModal 
        isOpen={showProductDetailsModal} 
        onClose={() => {
          setShowProductDetailsModal(false);
          setSelectedProduct(null);
        }}
        product={selectedProduct}
      />
      
      {/* Login Modal */}
      <LoginModal 
        isOpen={showLoginModal} 
        onClose={() => setShowLoginModal(false)} 
      />
    </div>
  );
};

// Product Card Component for Subcategory Page
const ProductListCard = ({ product, onInquireClick, onViewMobileClick, isInInquiry, addedProducts }) => {
  const { formatPriceRange } = useCurrency();
  const navigate = useNavigate();
  
  const handleClick = () => {
    navigate(`/product/${product.id}`);
  };

  const isAdded = isInInquiry ? isInInquiry(product.id) : false;
  const showAdded = addedProducts?.[product.id] === true;

  return (
    <div className="sp-product-card" onClick={handleClick}>
      <div className="sp-product-image-container">
        <img 
          src={product.image || product.images?.[0] || '/uploads/builder-images/328a0f6dd6e767b8279791bf791abd79bf7b4c61_w226.png'} 
          alt={product.name || product.description} 
          className="sp-product-image" 
        />
      </div>
      
      <div className="sp-product-content">
        <div className="sp-product-info">
          <h3 className="sp-product-name">{product.name || 'Product'}</h3>
          <div className="sp-product-pricing">
            <span className="sp-price-value">
              {formatPriceRange(product.priceMin || product.price || 2.5, product.priceMax || (product.price ? product.price * 1.2 : 3.2))}
            </span>
            <span className="sp-price-per">/ {product.pricePer || 'Tons'}</span>
          </div>
          <p className="sp-moq">
            <span className="sp-moq-label">(MOQ) </span>
            {product.moq || product.minOrder || 'I5 Tons'}
          </p>
        </div>
        
        <div className="sp-product-badges">
          <div className="sp-badge-row">
            {(() => {
              const planConfig = getPlanConfig(product.seller?.membershipPlan || product.membershipType);
              return (
                <div className="sp-badge sp-global-badge" style={{ backgroundColor: planConfig.color }}>
                  <img 
                    src={planConfig.icon} 
                    alt={planConfig.label} 
                    className="sp-badge-icon"
                  />
                  <span>{planConfig.label}</span>
                </div>
              );
            })()}
            {shouldShowVerifiedBadge(product.membershipPlan || product.seller?.membershipPlan) && (
              <div className="sp-verified-badge">
                <svg width="22" height="30" viewBox="0 0 22 30" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M22 15C22 15.9323 20.7329 16.6871 20.5034 17.5472C20.2653 18.4377 20.9782 19.7221 20.528 20.5015C20.0705 21.2923 18.597 21.314 17.9562 21.9562C17.314 22.5984 17.2923 24.0705 16.5014 24.528C15.7221 24.9782 14.4377 24.2653 13.5472 24.5034C12.6871 24.7329 11.9337 26 11 26C10.0663 26 9.31291 24.7329 8.45276 24.5034C7.56231 24.2653 6.27788 24.9782 5.49856 24.528C4.70769 24.0705 4.68605 22.597 4.04382 21.9562C3.4016 21.3154 1.92954 21.2923 1.47205 20.5015C1.02177 19.7221 1.73472 18.4377 1.49659 17.5472C1.26712 16.6871 0 15.9338 0 15C0 14.0663 1.26712 13.3129 1.49659 12.4528C1.73472 11.5623 1.02177 10.2779 1.47205 9.49857C1.92954 8.7077 3.40305 8.68605 4.04382 8.04383C4.68605 7.40161 4.70769 5.92955 5.49856 5.47206C6.27788 5.02178 7.56231 5.73472 8.45276 5.49659C9.31291 5.26712 10.0663 4 11 4C11.9337 4 12.6871 5.26712 13.5472 5.49659C14.4377 5.73472 15.7221 5.02178 16.5014 5.47206C17.2923 5.92955 17.314 7.40305 17.9562 8.04383C18.5984 8.68461 20.0705 8.7077 20.528 9.49857C20.9782 10.2779 20.2653 11.5623 20.5034 12.4528C20.7329 13.3129 22 14.0663 22 15Z" fill="#29ABE2"/>
                  <path d="M16.5881 11.6015C16.1234 11.1368 15.3701 11.1368 14.9054 11.6015L10.0317 16.4751L8.00835 14.4518C7.54364 13.9871 6.79029 13.9871 6.32558 14.4518C5.86087 14.9165 5.86087 15.6698 6.32558 16.1345L9.14847 18.9574C9.39237 19.2013 9.7142 19.3168 10.0331 19.3052C10.3521 19.3168 10.6739 19.2013 10.9178 18.9574L16.5896 13.2857C17.0543 12.821 17.0543 12.0676 16.5896 11.6029L16.5881 11.6015Z" fill="white"/>
                </svg>
                <span>Verified</span>
              </div>
            )}
          </div>
          
          <img 
            src="/uploads/builder-images/8c0d270e930b0365d2b9bc2dcd7dae8d6b170333_w332.png" 
            alt="Container" 
            className="sp-container-image"
          />
          
          <div className="sp-seller-info">
            <div className="sp-country-flag">
              <img 
                src="/uploads/builder-images/417568b6eb0fd8d1e09618ea0296738b87ee2e2a_w62.png" 
                alt="Flag" 
                className="sp-flag-small"
              />
            </div>
            <span className="sp-country-text">
              <span className="sp-country-name">{product.country || 'USA'} / </span>
              <span className="sp-member-years">0{product.memberYears || 1} Year</span>
            </span>
          </div>
          
          <div className="sp-business-type">
            <span>{product.businessType || 'Manufacture'}</span>
          </div>
        </div>
        
        <div className="sp-product-actions">
          <button 
            className={`sp-btn-inquire ${isAdded ? 'added' : ''}`} 
            onClick={(e) => onInquireClick && onInquireClick(product, e)}
          >
            {showAdded ? '✓ Added!' : isAdded ? 'Remove' : <><span className="desktop-text">Add to Quotation</span><span className="mobile-text">+ to quote</span></>}
          </button>
          <button 
            className="sp-btn-call" 
            onClick={(e) => {
              e.stopPropagation();
              const phone = product.seller?.phone || product.seller?.phoneNumber || product.phone;
              if (phone) {
                window.location.href = `tel:${phone}`;
              } else {
                alert('Phone number not available for this seller');
              }
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M22 16.92V19.92C22 20.4704 21.7893 20.9981 21.4142 21.3981C21.0391 21.7982 20.5304 22.0362 19.98 22.0699C19.643 22.0899 19.2989 22.0999 18.95 22.0999C10.21 22.0999 3.10001 14.9899 3.10001 6.24994C3.10001 5.90094 3.11001 5.55694 3.13001 5.21994C3.16376 4.66947 3.40177 4.16077 3.80183 3.78568C4.20189 3.41059 4.72954 3.19994 5.28001 3.19994H8.28001C8.72424 3.19557 9.15598 3.35553 9.49332 3.64969C9.83067 3.94386 10.0507 4.35171 10.11 4.79194C10.2199 5.66094 10.4293 6.51394 10.73 7.32994C10.8567 7.66661 10.8837 8.03161 10.808 8.38227C10.7323 8.73294 10.5571 9.05427 10.3 9.30994L9.00001 10.6099C10.3612 13.1755 12.4245 15.2387 14.99 16.5999L16.29 15.2999C16.5457 15.0429 16.8671 14.8677 17.2177 14.792C17.5684 14.7163 17.9334 14.7433 18.27 14.8699C19.086 15.1707 19.9391 15.3801 20.808 15.4899C21.2532 15.5498 21.6651 15.7746 21.9605 16.1183C22.256 16.462 22.4131 16.9009 22.4 17.3499L22 16.92Z" stroke="#6D6D6D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Call
          </button>
        </div>
      </div>
    </div>
  );
};

export default SubcategoryPage;
